// Time-Series Portfolio Line Chart.
//
// Dependency-free SVG chart plotting portfolio value over time.
// Features hover interactions, gradient fill, and visual indicators
// for years where a market shock was applied.

import { useMemo, useState } from 'react';
import type { ChartDataPoint } from '../api/stressTestApi';

export interface StressTestChartProps {
    data: ChartDataPoint[];
    currency?: string;
}

// ---------------------------------------------------------------------------
// Chart Dimensions & Layout (SVG Coordinate Space)
// ---------------------------------------------------------------------------
const VIEW_W = 800;
const VIEW_H = 400;
const PADDING_L = 70; // Left padding (room for Y-axis labels)
const PADDING_R = 40; // Right padding
const PADDING_T = 40; // Top padding
const PADDING_B = 50; // Bottom padding (room for X-axis labels)

const PLOT_W = VIEW_W - PADDING_L - PADDING_R;
const PLOT_H = VIEW_H - PADDING_T - PADDING_B;

function formatCurrency(value: number, currency: string): string {
    if (Math.abs(value) >= 1_000_000) {
        return `${currency}${(value / 1_000_000).toFixed(2)}M`;
    }
    if (Math.abs(value) >= 1_000) {
        return `${currency}${(value / 1_000).toFixed(1)}k`;
    }
    return `${currency}${value.toLocaleString(undefined, {
        maximumFractionDigits: 0,
    })}`;
}

export const StressTestChart = ({
    data,
    currency = '$',
}: StressTestChartProps) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    // -----------------------------------------------------------------------
    // 1. Math & Scaling
    // -----------------------------------------------------------------------
    // NOTE: hooks must run unconditionally, so we compute the scales
    // before the early-return guard below. When ``data`` is empty the
    // guard renders the placeholder and these memos are simply unused.
    const { minVal, maxVal, maxYear } = useMemo(() => {
        if (!data || data.length === 0) {
            return { minVal: 0, maxVal: 1, maxYear: 1 };
        }
        const vals = data.map((d) => d.portfolio_value);
        let max = Math.max(...vals, 1);
        const min = Math.min(...vals, 0); // Floor at 0 unless portfolio went negative

        // Add a 10% vertical pad above the highest point so the line doesn't hit the ceiling
        max += (max - min) * 0.1 || max * 0.1;
        return {
            minVal: min,
            maxVal: max,
            maxYear: Math.max(...data.map((d) => d.year), 1),
        };
    }, [data]);

    // Helpers to map data space (years & dollars) -> pixel space (X & Y)
    const getX = (year: number) => PADDING_L + (year / maxYear) * PLOT_W;
    const getY = (value: number) => {
        const range = maxVal - minVal;
        const normalized = range === 0 ? 0 : (value - minVal) / range;
        // SVG Y-axis goes top-down, so we invert the Y calculation
        return PADDING_T + PLOT_H - normalized * PLOT_H;
    };

    // -----------------------------------------------------------------------
    // 2. Build the SVG Paths
    // -----------------------------------------------------------------------
    const { linePath, areaPath } = useMemo(() => {
        if (!data || data.length < 2) {
            return { linePath: '', areaPath: '' };
        }
        const path = data
            .map(
                (d, i) =>
                    `${i === 0 ? 'M' : 'L'} ${getX(d.year)} ${getY(d.portfolio_value)}`,
            )
            .join(' ');
        const bottomY = PADDING_T + PLOT_H;
        // Close the area path down to the baseline
        const area = `${path} L ${getX(data[data.length - 1].year)} ${bottomY} L ${getX(data[0].year)} ${bottomY} Z`;
        return { linePath: path, areaPath: area };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, minVal, maxVal, maxYear]);

    // If there's no data or only a single point (horizon = 0), we can't draw a line chart.
    if (!data || data.length < 2) {
        return (
            <div className="flex h-full w-full items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800">
                <span className="text-sm font-semibold muted px-4 text-center">
                    Increase time horizon to &gt; 0 years to see the projection trajectory.
                </span>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // 3. Render
    // -----------------------------------------------------------------------
    return (
        <div className="w-full h-full min-h-0 select-none relative group">
            <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                className="w-full h-full overflow-visible touch-none"
                preserveAspectRatio="xMidYMid meet"
                onMouseLeave={() => setHoverIndex(null)}
            >
                <defs>
                    <linearGradient
                        id="area-gradient"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                </defs>

                {/* --- Gridlines (Y-Axis) --- */}
                {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                    const y = PADDING_T + PLOT_H * (1 - tick);
                    const val = minVal + (maxVal - minVal) * tick;
                    return (
                        <g key={`y-tick-${tick}`}>
                            <line
                                x1={PADDING_L}
                                x2={VIEW_W - PADDING_R}
                                y1={y}
                                y2={y}
                                stroke="currentColor"
                                strokeOpacity={0.1}
                                strokeDasharray="4 4"
                            />
                            <text
                                x={PADDING_L - 10}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="12"
                                fontWeight="500"
                                className="fill-slate-400 dark:fill-slate-500"
                            >
                                {formatCurrency(val, currency)}
                            </text>
                        </g>
                    );
                })}

                {/* --- Data Fill Area --- */}
                <path
                    d={areaPath}
                    fill="url(#area-gradient)"
                    className="transition-all duration-300"
                />

                {/* --- Data Line --- */}
                <path
                    d={linePath}
                    fill="none"
                    stroke="#10b981" // emerald-500
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transition-all duration-300"
                />

                {/* --- X-Axis (Years) Baseline --- */}
                <line
                    x1={PADDING_L}
                    x2={VIEW_W - PADDING_R}
                    y1={PADDING_T + PLOT_H}
                    y2={PADDING_T + PLOT_H}
                    stroke="currentColor"
                    strokeOpacity={0.4}
                    strokeWidth="1.5"
                />

                {/* X-Axis Labels (Start, Middle, End) */}
                {[0, Math.floor(maxYear / 2), maxYear].map((yr, idx, arr) => {
                    // Prevent duplicate labels if horizon is very short
                    if (idx > 0 && yr === arr[idx - 1]) return null;
                    return (
                        <text
                            key={`x-label-${yr}`}
                            x={getX(yr)}
                            y={PADDING_T + PLOT_H + 20}
                            textAnchor="middle"
                            fontSize="12"
                            fontWeight="600"
                            className="fill-slate-400 dark:fill-slate-500"
                        >
                            Year {yr}
                        </text>
                    );
                })}

                {/* --- Interactive Hover Zones & Crosshair --- */}
                {data.map((point, i) => {
                    const cx = getX(point.year);
                    const cy = getY(point.portfolio_value);

                    // Invisible hit-area slices that partition the chart horizontally
                    const prevX = i > 0 ? getX(data[i - 1].year) : PADDING_L;
                    const nextX =
                        i < data.length - 1
                            ? getX(data[i + 1].year)
                            : VIEW_W - PADDING_R;
                    const zoneLeft = (prevX + cx) / 2;
                    const zoneRight = (cx + nextX) / 2;

                    const isHovered = hoverIndex === i;

                    return (
                        <g key={`hover-zone-${point.year}`}>
                            <rect
                                x={zoneLeft}
                                y={PADDING_T}
                                width={zoneRight - zoneLeft}
                                height={PLOT_H}
                                fill="transparent"
                                onMouseEnter={() => setHoverIndex(i)}
                                className="cursor-crosshair outline-none"
                            />

                            {/* Always show red dots for shock events */}
                            {point.is_shock_year && !isHovered && (
                                <circle
                                    cx={cx}
                                    cy={cy}
                                    r="4.5"
                                    fill="#ef4444" // red-500
                                    stroke="#ffffff"
                                    strokeWidth="1.5"
                                    className="dark:stroke-slate-900 pointer-events-none"
                                />
                            )}

                            {/* Render the Crosshair & Tooltip if this specific point is hovered */}
                            {isHovered && (
                                <g className="pointer-events-none animate-fade-in">
                                    {/* Vertical Crosshair Line */}
                                    <line
                                        x1={cx}
                                        x2={cx}
                                        y1={PADDING_T}
                                        y2={PADDING_T + PLOT_H}
                                        stroke="#10b981"
                                        strokeWidth="1"
                                        strokeDasharray="4 4"
                                    />

                                    {/* Data Point Highlight */}
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r="6"
                                        fill={point.is_shock_year ? '#ef4444' : '#10b981'}
                                        stroke="#fff"
                                        strokeWidth="2"
                                        className="dark:stroke-slate-900 drop-shadow-sm"
                                    />

                                    {/* Tooltip Background (Shifts left if near right edge) */}
                                    <rect
                                        x={cx > VIEW_W / 2 ? cx - 150 : cx + 20}
                                        y={Math.max(PADDING_T, cy - 40)}
                                        width="130"
                                        height="50"
                                        rx="6"
                                        className="fill-slate-800 dark:fill-slate-100 drop-shadow-md"
                                    />
                                    {/* Tooltip Text - Year */}
                                    <text
                                        x={cx > VIEW_W / 2 ? cx - 85 : cx + 85}
                                        y={Math.max(PADDING_T, cy - 40) + 18}
                                        textAnchor="middle"
                                        fontSize="11"
                                        fontWeight="700"
                                        className={
                                            point.is_shock_year
                                                ? 'fill-red-400 dark:fill-red-500'
                                                : 'fill-slate-400 dark:fill-slate-500'
                                        }
                                    >
                                        Year {point.year}{' '}
                                        {point.is_shock_year && '(Shock)'}
                                    </text>
                                    {/* Tooltip Text - Value */}
                                    <text
                                        x={cx > VIEW_W / 2 ? cx - 85 : cx + 85}
                                        y={Math.max(PADDING_T, cy - 40) + 36}
                                        textAnchor="middle"
                                        fontSize="14"
                                        fontWeight="800"
                                        className="fill-white dark:fill-slate-900"
                                    >
                                        {formatCurrency(point.portfolio_value, currency)}
                                    </text>
                                </g>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Simple legend */}
            <div className="absolute top-0 right-2 flex gap-4 text-[10px] font-bold uppercase tracking-wide muted opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" /> Projected
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-red-500" /> Shock
                </div>
            </div>
        </div>
    );
};

export default StressTestChart;
